import * as React from "react";
import { AuthLayout } from "../layout";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../mobx/components/index";
import { Link } from "../../../components/router/Link";
import { UI } from "../../../../core/ui";
import { validators } from "@lib/common";
import { logger } from "@lib/common";
import { LinkTag } from "@lib/components";
import { genRecaptchaToken, loadRecaptchaScript } from '../../../../core/libs/grecaptcha';
import {
	AuthHeading,
	AuthSubheading,
	FooterLinks,
	FormField,
	FormInput,
	FormLabel,
	InfoText,
	SubmitButton,
} from "./login.form.c";

interface Props { }
interface State {
	loading: boolean;
	email: string;
	success: boolean;
}

@inject("store") @observer
export class AuthPasswordResetInit extends MobxComponent<Props, State> {
	async componentDidMount() {
		try {
			await loadRecaptchaScript();
			console.log("reCAPTCHA script loaded successfully!");
		} catch (error) {
			console.error("Error loading reCAPTCHA script", error);
		}
	}

	constructor(props: Props) {
		super(props);
		this.state = {
			loading: false,
			email: "",
			success: false,
		};
	}

	load = (loading: boolean) => {
		this.setState({ loading });
	}

	onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const { store } = this.injected;
		try {

			let email = this.state.email;
			let validateToken = await genRecaptchaToken('reset_password');

			if (!email) {
				return UI.notification.error("Please enter your account e-mail");
			}

			const email_valid = validators.email(email);
			if (email_valid.error) {
				return UI.notification.error("Invalid e-mail address");
			}

			email = email_valid.value;

			this.load(true);

			const data = await store.api.user_password_reset_init({ email, validateToken });

			this.load(false);


			if (data.outcome) {
				return UI.notification.error(data.message, { timeout: 6000 });
			} else {
				this.setState({ success: true, email: "" });
			}

		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Email address not found", { timeout: 6000});
			this.load(false);
		}
		return null;
	}

	onChange = <T extends keyof State>(e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = UI.helpers.handleChange(e);
		this.setState(newState as { [P in T]: State[P]; });
	}

	render() {
		const { email, loading, success } = this.state;
		return (
			<AuthLayout loading={loading}>
				<AuthHeading>Reset password</AuthHeading>
				{!success ? (
					<>
						<AuthSubheading>Enter your account email and we&apos;ll send you a reset link.</AuthSubheading>
						<form onSubmit={this.onSubmit}>
							<FormField>
								<FormLabel htmlFor="reset-email">Email address</FormLabel>
								<FormInput
									id="reset-email"
									type="email"
									name="email"
									placeholder="you@example.com"
									value={email}
									onChange={this.onChange}
									autoComplete="email"
									required={true}
									disabled={loading}
								/>
							</FormField>

							<SubmitButton type="submit" disabled={loading}>
								{loading ? (
									<>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
											<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
											<path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" opacity="0.75" />
										</svg>
										Sending…
									</>
								) : (
									"Reset Password"
								)}
							</SubmitButton>

							<FooterLinks>
								<p>Don&apos;t have an account? <Link to="/register">Sign Up</Link></p>
								<p>Remember your password? <Link to="/login">Login</Link></p>
							</FooterLinks>
						</form>
					</>
				) : (
					<>
						<InfoText>
							An e-mail has been sent to your address.
							Please follow the link inside the e-mail to reset your password.
							If you have not received a mail, check your spam or{" "}
							<LinkTag onClick={() => this.setState({ success: false })}>try again</LinkTag>
						</InfoText>
						<FooterLinks>
							<p>Don&apos;t have an account? <Link to="/register">Sign Up</Link></p>
							<p>Remember your password? <Link to="/login">Login</Link></p>
						</FooterLinks>
					</>
				)}
			</AuthLayout>
		);
	}

}
